import { Checkbox, LoadingSpinner, Text } from "@/components/ui";
import { FileThumbnail } from "@/components/ui/file-thumbnail";
import { cn } from "@/lib/utils/cn";
import {
  ChevronRight,
  CircleAlert,
  File,
  Folder,
  FolderInput,
  Eye,
} from "lucide-react";
import type React from "react";
import type { ReactNode } from "react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  type DropTargetMonitor,
  useDrag,
  useDragLayer,
  useDrop,
} from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import toast from "react-hot-toast";
import { type FileSortKey } from "./FileRepositorySort";

import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import { getItemFromPresignedUrlOptions } from "@/lib/data/react-query/s3";
import { type MatterFile } from "@/lib/types";
import {
  getFileIcon,
  getFileNameParts,
  getFileMoveConflictMessage,
  getIsFileImage,
  getMatterImageTypeMessage,
  isCriticalImage,
  sortFiles,
} from "@/lib/utils/matterFile";
import { useQuery } from "@tanstack/react-query";
import type { RepoItem } from "./FileRepository";
import { getSelectedRepositoryRoots } from "./fileRepositorySelection";

type DragPayload = {
  items: RepoItem[];
};

function isDragPayload(item: unknown): item is DragPayload {
  return (
    typeof item === "object" &&
    item !== null &&
    "items" in item &&
    Array.isArray(item.items)
  );
}

function FileDragPreview() {
  const { currentOffset, isDragging, item } = useDragLayer((monitor) => ({
    currentOffset: monitor.getClientOffset(),
    isDragging: monitor.isDragging(),
    item: monitor.getItem<unknown>(),
  }));

  const items = isDragPayload(item) ? item.items : [];
  if (
    !isDragging ||
    !currentOffset ||
    items.length === 0 ||
    items[0]?.isFolder
  ) {
    return null;
  }

  const isMultiFileDrag = items.length > 1;

  return (
    <div
      className="pointer-events-none fixed top-0 left-0 z-[100] w-56"
      style={{
        transform: `translate(${currentOffset.x + 12}px, ${currentOffset.y + 12}px)`,
      }}
    >
      {isMultiFileDrag && (
        <>
          <div className="bg-card/80 absolute inset-x-2 top-2 h-full rounded border shadow-sm" />
          <div className="bg-card/90 absolute inset-x-1 top-1 h-full rounded border shadow-sm" />
        </>
      )}
      <div className="bg-card relative flex items-center gap-2 rounded border px-3 py-2 shadow-md">
        <File className="h-4 w-4 shrink-0 text-blue-500" />
        <Text as="span" variant="subHeading" className="truncate">
          {isMultiFileDrag ? `${items.length} files` : items[0]?.name}
        </Text>
      </div>
    </div>
  );
}

export const TREE_ICON_CLASS = "h-5 w-5 shrink-0";
export const TREE_ROW_CLASS =
  "group hover:bg-muted/50 flex cursor-pointer items-center rounded px-2 py-2 transition-colors";
export const TREE_TEXT_CLASS =
  "block min-w-0 flex-1 truncate text-nowrap break-normal text-sm leading-5";
export const TREE_CONTENT_CLASS =
  "flex min-w-0 flex-1 items-center gap-2 origin-left transition-transform duration-150 group-hover:scale-[1.03]";

export function getTreeCheckboxClassName(
  checkboxState: boolean | "indeterminate" | undefined,
) {
  const isSelected =
    checkboxState === true || checkboxState === "indeterminate";

  return cn(
    "mr-2 w-4 shrink-0 transition-opacity duration-150",
    isSelected
      ? "opacity-100"
      : "pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100",
  );
}

export function getTreeIndent(level: number) {
  return level * 16 + 4;
}

type FolderTreeProps = {
  matter: { matterTitle: string };
  items: RepoItem[];
  selectionItems?: RepoItem[];
  showCheckboxes?: boolean;
  selectedItemIds?: Set<string>;
  selectedDragItems?: RepoItem[];
  selectionMode?: "all" | "files-only";
  onToggleSelection?: (items: RepoItem[], checked: boolean) => void;
  onToggleRootSelection?: (checked: boolean) => void;
  onSelectItem: (itemId: string) => void;
  selectedItemId: string | null;
  onMoveItem: (
    item: RepoItem,
    targetFolderId: string,
  ) => boolean | Promise<boolean>;
  onMoveItems?: (
    items: RepoItem[],
    targetFolderId: string,
  ) => void | Promise<void>;
  enableDrag?: boolean;
  onNavigateToFolder?: (folderId: string) => void;
  onCreateFolder?: () => void;
  currentFolderId: string;
  enabledFileTypes?: string[];
  isSearching?: boolean;
  sortKey?: FileSortKey;
  showHeader?: boolean;
  expandRootFoldersWhenHeaderHidden?: boolean;
  previewLoadingItemId?: string | null;
  movingItemIds?: Set<string>;
  dragType?: string;
  renderContextMenu?: (item: RepoItem, trigger: ReactNode) => React.JSX.Element;
};

export function FolderTree({
  matter,
  items,
  selectionItems,
  showCheckboxes = false,
  selectedItemIds,
  selectedDragItems,
  selectionMode = "all",
  onToggleSelection,
  onToggleRootSelection,
  onSelectItem,
  selectedItemId,
  onMoveItem,
  onMoveItems,
  enableDrag = true,
  onNavigateToFolder,
  currentFolderId,
  enabledFileTypes,
  isSearching = false,
  sortKey,
  showHeader = true,
  expandRootFoldersWhenHeaderHidden = true,
  previewLoadingItemId = null,
  movingItemIds,
  dragType = "FILE_SYSTEM_ITEM",
  renderContextMenu,
}: FolderTreeProps) {
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
    new Set(["root"]),
  );
  const selectionAnchorIdRef = useRef<string | null>(null);
  const hasAutoExpandedHiddenRootFoldersRef = useRef(false);
  const lastAutoExpandedFolderPathKeyRef = useRef<string | null>(null);

  const handleMoveItems = useCallback(
    async (selectedItems: RepoItem[], targetFolderId: string) => {
      const repositoryItems = selectionItems ?? items;
      const itemsToMove = getSelectedRepositoryRoots(
        selectedItems,
        repositoryItems,
      );
      const conflict = getFileMoveConflictMessage(
        itemsToMove.filter((item) => item.kind !== "share-folder"),
        repositoryItems.filter((item) => item.kind !== "share-folder"),
        targetFolderId,
      );
      if (conflict) {
        toast.error(conflict);
        return;
      }
      if (onMoveItems) {
        await onMoveItems(itemsToMove, targetFolderId);
      } else {
        await Promise.all(
          itemsToMove.map((item) => onMoveItem(item, targetFolderId)),
        );
      }
    },
    [items, selectionItems, onMoveItem, onMoveItems],
  );

  const toggleFolder = useCallback(
    (folderId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      const newExpandedFolders = new Set(expandedFolders);
      if (newExpandedFolders.has(folderId)) {
        newExpandedFolders.delete(folderId);
      } else {
        newExpandedFolders.add(folderId);
      }
      setExpandedFolders(newExpandedFolders);
    },
    [expandedFolders],
  );

  // Handle item selection with folder navigation for files
  const handleSelectItem = useCallback(
    (item: MatterFile) => {
      onSelectItem(item.id);
      if (showCheckboxes && !item.isFolder) {
        selectionAnchorIdRef.current = item.id;
      }

      // If it's a file, navigate to its parent folder
      if (!item.isFolder && onNavigateToFolder) {
        onNavigateToFolder(item.idLocation ?? "");

        // Also make sure the parent folder is expanded
        if (item.idLocation) {
          setExpandedFolders((prev) => {
            const newSet = new Set(prev);

            // Create the immediate parent
            newSet.add(item.idLocation ?? "");

            // Also expand all ancestor folders
            let currentParentId = item.idLocation;
            while (currentParentId) {
              const parentFolder = items.find(
                (i) => i.id === currentParentId && i.isFolder,
              );
              if (parentFolder && parentFolder.idLocation) {
                newSet.add(parentFolder.idLocation);
                currentParentId = parentFolder.idLocation;
              } else {
                break;
              }
            }

            return newSet;
          });
        }
      }
    },
    [onNavigateToFolder, items, onSelectItem, showCheckboxes],
  );

  const selectionChildrenByParent = useMemo(() => {
    const map = new Map<string, RepoItem[]>();
    (selectionItems ?? items).forEach((item) => {
      const parentId = item.idLocation || "";
      const siblings = map.get(parentId) ?? [];
      siblings.push(item);
      map.set(parentId, siblings);
    });
    return map;
  }, [items, selectionItems]);

  const isRootExpanded = expandedFolders.has("root");

  const isSelectableFile = useCallback(
    (item: RepoItem) => {
      if (item.isFolder) return false;
      if (!enabledFileTypes) return true;
      return enabledFileTypes.includes(
        getFileNameParts(item.name).fileExtension,
      );
    },
    [enabledFileTypes],
  );

  const getDescendantIds = useCallback(
    (folderId: string) => {
      const ids = new Set<string>();
      const stack = [folderId];
      while (stack.length > 0) {
        const currentId = stack.pop();
        if (!currentId) continue;
        const children = selectionChildrenByParent.get(currentId) ?? [];
        children.forEach((child) => {
          if (
            !child.isFolder &&
            enabledFileTypes &&
            !enabledFileTypes.includes(
              getFileNameParts(child.name).fileExtension,
            )
          ) {
            return;
          }
          if (!child.isFolder || selectionMode === "all") {
            ids.add(child.id);
          }
          if (child.isFolder) {
            stack.push(child.id);
          }
        });
      }
      return ids;
    },
    [enabledFileTypes, selectionChildrenByParent, selectionMode],
  );

  const getSelectionState = useCallback(
    (ids: Set<string>): boolean | "indeterminate" => {
      if (!selectedItemIds || ids.size === 0) return false;
      let selectedCount = 0;
      ids.forEach((id) => {
        if (selectedItemIds.has(id)) {
          selectedCount += 1;
        }
      });
      if (selectedCount === 0) return false;
      if (selectedCount === ids.size) return true;
      return "indeterminate";
    },
    [selectedItemIds],
  );

  const getOrderedChildren = useCallback(
    (parentId: string) => {
      const children = items.filter(
        (item) => (item.idLocation ?? "") === parentId,
      );
      const folders = children
        .filter((item) => item.isFolder)
        .sort((a, b) => a.name.localeCompare(b.name));
      const files = sortKey
        ? sortFiles(
            children.filter((item) => !item.isFolder),
            sortKey,
          )
        : children
            .filter((item) => !item.isFolder)
            .sort((a, b) => a.name.localeCompare(b.name));
      return [...folders, ...files];
    },
    [items, sortKey],
  );

  const visibleSelectableItems = useMemo(() => {
    if (showHeader && !isRootExpanded) return [] as RepoItem[];
    const ordered: RepoItem[] = [];
    const walk = (parentId: string) => {
      const children = getOrderedChildren(parentId);
      children.forEach((child) => {
        if (isSelectableFile(child)) {
          ordered.push(child);
        }
        if (child.isFolder && expandedFolders.has(child.id)) {
          walk(child.id);
        }
      });
    };
    walk("");
    return ordered;
  }, [
    expandedFolders,
    getOrderedChildren,
    isRootExpanded,
    isSelectableFile,
    showHeader,
  ]);

  const selectableIndexById = useMemo(() => {
    const map = new Map<string, number>();
    visibleSelectableItems.forEach((item, index) => {
      map.set(item.id, index);
    });
    return map;
  }, [visibleSelectableItems]);

  useEffect(() => {
    if (selectedItemIds && selectedItemIds.size === 0) {
      selectionAnchorIdRef.current = null;
    }
  }, [selectedItemIds]);

  const handleSelectionToggle = useCallback(
    (item: RepoItem, checked: boolean, shiftKey?: boolean) => {
      if (!onToggleSelection) return;
      const selectable = isSelectableFile(item);

      if (!shiftKey || !selectable) {
        onToggleSelection([item], checked);
        if (selectable) {
          selectionAnchorIdRef.current = item.id;
        }
        return;
      }

      const anchorId = selectionAnchorIdRef.current;
      const anchorIndex = anchorId
        ? selectableIndexById.get(anchorId)
        : undefined;
      const targetIndex = selectableIndexById.get(item.id);

      if (anchorIndex === undefined || targetIndex === undefined) {
        onToggleSelection([item], true);
        selectionAnchorIdRef.current = item.id;
        return;
      }

      const start = Math.min(anchorIndex, targetIndex);
      const end = Math.max(anchorIndex, targetIndex);
      onToggleSelection(visibleSelectableItems.slice(start, end + 1), true);
    },
    [
      isSelectableFile,
      onToggleSelection,
      selectableIndexById,
      visibleSelectableItems,
    ],
  );

  const renderFolder = useCallback(
    (folderId: string, level = 0): React.JSX.Element | null => {
      const folder = items.find(
        (item) => item.id === folderId && item.isFolder,
      );
      if (!folder) return null;

      const isExpanded = expandedFolders.has(folderId);
      const isSelected = selectedItemId === folderId;
      const descendantIds = getDescendantIds(folderId);
      if (selectionMode === "all") descendantIds.add(folderId);
      const checkboxState = showCheckboxes
        ? descendantIds.size > 0
          ? getSelectionState(descendantIds)
          : selectionMode === "files-only"
            ? false
            : getSelectionState(new Set([folderId]))
        : false;

      // Get direct children
      const children = items.filter(
        (item) => (item.idLocation ?? "") === folderId,
      );

      return (
        <FolderComponent
          key={folderId}
          folder={folder}
          isExpanded={isExpanded}
          isSelected={isSelected}
          // children={children}
          level={level}
          onSelectItem={handleSelectItem}
          toggleFolder={toggleFolder}
          onMoveItem={onMoveItem}
          onMoveItems={handleMoveItems}
          renderFolder={renderFolder}
          selectedItemId={selectedItemId}
          selectedItemIds={selectedItemIds}
          selectedDragItems={selectedDragItems}
          isSearching={isSearching}
          enabledFileTypes={enabledFileTypes}
          sortKey={sortKey}
          showCheckboxes={showCheckboxes}
          checkboxState={checkboxState}
          onToggleSelection={handleSelectionToggle}
          getSelectionState={getSelectionState}
          enableDrag={enableDrag}
          movingItemIds={movingItemIds}
          dragType={dragType}
          renderContextMenu={renderContextMenu}
        >
          {children}
        </FolderComponent>
      );
    },
    [
      items,
      expandedFolders,
      selectedItemId,
      selectedItemIds,
      selectedDragItems,
      onMoveItem,
      handleMoveItems,
      enabledFileTypes,
      handleSelectItem,
      toggleFolder,
      isSearching,
      sortKey,
      showCheckboxes,
      handleSelectionToggle,
      getSelectionState,
      getDescendantIds,
      enableDrag,
      movingItemIds,
      dragType,
      selectionMode,
      renderContextMenu,
    ],
  );

  // Get root folders
  const rootFolders = items
    .filter((item) => item.isFolder && (item.idLocation ?? "") === "")
    .sort((a, b) => a.name.localeCompare(b.name));

  // Get root files
  const filteredRootFiles = items.filter(
    (item) => !item.isFolder && (item.idLocation ?? "") === "",
  );

  const rootFiles = sortKey
    ? sortFiles(filteredRootFiles, sortKey)
    : filteredRootFiles;
  const rootFolderIds = useMemo(
    () =>
      items
        .filter((item) => item.isFolder && (item.idLocation ?? "") === "")
        .map((item) => item.id),
    [items],
  );

  const rootSelectionState = useMemo(() => {
    if (!showCheckboxes || !selectedItemIds) return false;
    const ids = new Set(
      (selectionItems ?? items)
        .filter((item) => selectionMode === "all" || !item.isFolder)
        .map((item) => item.id),
    );
    return getSelectionState(ids);
  }, [
    getSelectionState,
    items,
    selectedItemIds,
    selectionItems,
    selectionMode,
    showCheckboxes,
  ]);

  // Make the root a drop target for moving to root
  const [{ isOver: isOverRoot }, drop] = useDrop({
    accept: dragType,
    drop: (payload: DragPayload, monitor) => {
      if (monitor.didDrop()) {
        return; // Don't handle if a nested target already handled it
      }

      void handleMoveItems(payload.items, "");
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver({ shallow: true }),
    }),
  });

  // Check if there are any folders at all
  const hasFolders = items.some((item) => item.isFolder);

  // Expand the current path before paint so the tree never shows a stale shape.
  useLayoutEffect(() => {
    if (!currentFolderId) {
      lastAutoExpandedFolderPathKeyRef.current = null;
      return;
    }

    const folderPathIds = [currentFolderId];
    let parentId = currentFolderId;

    while (parentId) {
      const folder = items.find(
        (item) => item.id === parentId && item.isFolder,
      );
      if (!folder?.idLocation) {
        break;
      }

      folderPathIds.push(folder.idLocation);
      parentId = folder.idLocation;
    }

    const folderPathKey = folderPathIds.join("|");
    if (lastAutoExpandedFolderPathKeyRef.current === folderPathKey) {
      return;
    }

    lastAutoExpandedFolderPathKeyRef.current = folderPathKey;

    setExpandedFolders((prev) => {
      const newSet = new Set(prev);

      folderPathIds.forEach((folderId) => {
        newSet.add(folderId);
      });

      return newSet;
    });
  }, [currentFolderId, items]);

  useLayoutEffect(() => {
    if (showHeader || !expandRootFoldersWhenHeaderHidden) {
      hasAutoExpandedHiddenRootFoldersRef.current = false;
      return;
    }

    if (
      rootFolderIds.length === 0 ||
      hasAutoExpandedHiddenRootFoldersRef.current
    ) {
      return;
    }

    setExpandedFolders((prev) => {
      const newSet = new Set(prev);

      rootFolderIds.forEach((folderId) => {
        newSet.add(folderId);
      });

      return newSet;
    });
    hasAutoExpandedHiddenRootFoldersRef.current = true;
  }, [expandRootFoldersWhenHeaderHidden, rootFolderIds, showHeader]);

  return (
    <div
      ref={drop}
      className={cn(
        "overflow-auto rounded-md p-2",
        isOverRoot && "bg-primary/5 border-primary/20 border",
      )}
    >
      {hasFolders || rootFiles.length > 0 ? (
        <>
          {/* Root section with files */}
          <div>
            {showHeader && (
              <div
                className={cn(
                  TREE_ROW_CLASS,
                  currentFolderId === "" && "bg-primary/10 text-primary",
                )}
                onClick={() => {
                  onSelectItem("");
                  if (onNavigateToFolder) onNavigateToFolder("");
                }}
                title={matter.matterTitle}
              >
                {showCheckboxes && (
                  <Checkbox
                    checked={rootSelectionState}
                    onClick={(event) => {
                      event.stopPropagation();
                    }}
                    onCheckedChange={(value) => {
                      const nextChecked =
                        value === "indeterminate" ? true : value;
                      onToggleRootSelection?.(!!nextChecked);
                    }}
                    className={getTreeCheckboxClassName(rootSelectionState)}
                    aria-label="Select all items"
                  />
                )}
                <ChevronRight
                  className={cn(
                    "mr-1 transition-transform",
                    TREE_ICON_CLASS,
                    isRootExpanded && "rotate-90",
                  )}
                  onClick={(e) => {
                    toggleFolder("root", e);
                  }}
                />
                <div className={TREE_CONTENT_CLASS}>
                  <Folder className={cn("text-amber-500", TREE_ICON_CLASS)} />
                  <Text
                    variant="subHeading"
                    color="foreground"
                    as="span"
                    className={TREE_TEXT_CLASS}
                  >
                    {matter.matterTitle}
                  </Text>
                </div>
              </div>
            )}

            {(isRootExpanded || !showHeader) && (
              <div>
                {rootFolders.length === 0 && rootFiles.length === 0 ? (
                  <div
                    className="px-2 py-2 italic"
                    style={{ paddingLeft: `${getTreeIndent(1)}px` }}
                  >
                    <Text variant="subHeading" as="span">
                      {isSearching
                        ? "No results found"
                        : "This folder is empty"}
                    </Text>
                  </div>
                ) : (
                  <>
                    {/* Render root folders */}
                    {rootFolders.map((folder) => renderFolder(folder.id, 1))}

                    {/* Render root files */}
                    {rootFiles.map((item) => (
                      <FileTreeItem
                        key={item.id}
                        item={item}
                        level={0}
                        isSelected={
                          Boolean(showCheckboxes) &&
                          Boolean(selectedItemIds?.has(item.id))
                        }
                        dragItems={selectedDragItems}
                        isPreviewed={
                          !item.isFolder && item.id === selectedItemId
                        }
                        isPreviewLoading={item.id === previewLoadingItemId}
                        isMoveLoading={movingItemIds?.has(item.id)}
                        onSelect={handleSelectItem}
                        disabled={
                          enabledFileTypes &&
                          !enabledFileTypes.includes(
                            getFileNameParts(item.name).fileExtension,
                          )
                        }
                        showCheckboxes={showCheckboxes}
                        checkboxState={
                          showCheckboxes
                            ? getSelectionState(new Set([item.id]))
                            : false
                        }
                        onToggleSelection={handleSelectionToggle}
                        enableDrag={enableDrag}
                        dragType={dragType}
                        renderContextMenu={renderContextMenu}
                      />
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        </>
      ) : (
        <div
          className="px-2 py-2 italic"
          style={{ paddingLeft: `${getTreeIndent(1)}px` }}
        >
          <Text variant="subHeading" as="span">
            {isSearching ? "No results found" : "This folder is empty"}
          </Text>
        </div>
      )}
      <FileDragPreview />
    </div>
  );
}

// File item component with image thumbnail support
function FileTreeItem({
  item,
  level,
  isSelected,
  dragItems,
  isPreviewed,
  isPreviewLoading,
  isMoveLoading,
  onSelect,
  disabled,
  showCheckboxes,
  checkboxState,
  onToggleSelection,
  enableDrag = true,
  dragType = "FILE_SYSTEM_ITEM",
  renderContextMenu,
}: {
  item: RepoItem;
  level: number;
  isSelected: boolean;
  dragItems?: RepoItem[];
  isPreviewed?: boolean;
  isPreviewLoading?: boolean;
  isMoveLoading?: boolean;
  onSelect: (item: RepoItem) => void;
  disabled?: boolean;
  showCheckboxes?: boolean;
  checkboxState?: boolean | "indeterminate";
  onToggleSelection?: (
    item: RepoItem,
    checked: boolean,
    shiftKey?: boolean,
  ) => void;
  enableDrag?: boolean;
  dragType?: string;
  renderContextMenu?: (item: RepoItem, trigger: ReactNode) => React.JSX.Element;
}) {
  const isImage = getIsFileImage(item.name);
  const lastShiftKeyRef = useRef(false);

  const FileIcon = getFileIcon(item.name);
  const isCritical = isCriticalImage(item);
  const hasStatusIcons = isCritical;
  const showOverlayEye =
    Boolean(isPreviewed) && !Boolean(isPreviewLoading) && !hasStatusIcons;
  const showInlineEye =
    Boolean(isPreviewed) && !Boolean(isPreviewLoading) && hasStatusIcons;

  const {
    data: fileFromPresignedURLData,
    isError: isErrorLoadingFile,
    isLoading: isLoadingFileUrl,
  } = useQuery(
    getItemFromPresignedUrlOptions("matter-file", item.presignedUrlKey ?? ""),
  );
  const fileUrl = fileFromPresignedURLData?.url ?? null;

  const [{ isDragging }, drag, preview] = useDrag(
    () => ({
      type: dragType,
      item: {
        items: isSelected && dragItems?.length ? dragItems : [item],
      } satisfies DragPayload,
      canDrag: enableDrag && !disabled && !isMoveLoading,
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
    }),
    [
      disabled,
      dragItems,
      dragType,
      enableDrag,
      isMoveLoading,
      isSelected,
      item,
    ],
  );

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  const dragRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!enableDrag) return;
      drag(node);
    },
    [drag, enableDrag],
  );

  const trigger = (
    <div
      ref={dragRef}
      data-testid={`file-${item.name}`}
      className={cn(
        TREE_ROW_CLASS,
        "relative justify-between",
        isSelected && "bg-primary/10 text-primary hover:bg-primary/20",
        disabled && "text-muted-foreground/50",
        isDragging && "opacity-50",
        isMoveLoading && "cursor-wait",
      )}
      style={{ paddingLeft: `${getTreeIndent(level + 1)}px` }}
      onClick={
        disabled
          ? undefined
          : () => {
              onSelect(item);
            }
      }
    >
      {showOverlayEye && (
        <Eye className="pointer-events-none absolute top-1/2 right-2 h-5 w-5 -translate-y-1/2 text-blue-500" />
      )}
      {showCheckboxes && (
        <Checkbox
          checked={checkboxState ?? false}
          onClick={(event) => {
            event.stopPropagation();
            lastShiftKeyRef.current = event.shiftKey;
          }}
          onCheckedChange={(value) => {
            if (disabled) return;
            const nextChecked =
              value === "indeterminate" ? true : Boolean(value);
            const shiftKey = lastShiftKeyRef.current;
            lastShiftKeyRef.current = false;
            onToggleSelection?.(item, nextChecked, shiftKey);
          }}
          className={cn(
            getTreeCheckboxClassName(checkboxState),
            "relative z-10 after:absolute after:-inset-x-3 after:-inset-y-[9px] after:content-['']",
          )}
          aria-label={`Select ${item.name}`}
        />
      )}
      <div className={TREE_CONTENT_CLASS}>
        {isMoveLoading || isPreviewLoading ? (
          <LoadingSpinner className={cn(TREE_ICON_CLASS, "text-blue-500")} />
        ) : isImage ? (
          <FileThumbnail
            file={{
              name: item.name,
              type: "",
            }}
            className={cn(
              TREE_ICON_CLASS,
              "overflow-hidden rounded-sm border-0 shadow-none",
            )}
            previewClassName="bg-white"
            previewImageUrl={fileUrl}
            isLoading={isLoadingFileUrl}
            hasError={!fileUrl || isErrorLoadingFile}
          />
        ) : (
          <FileIcon
            className={cn(
              TREE_ICON_CLASS,
              "text-blue-500",
              disabled && "text-muted-foreground/50",
            )}
          />
        )}
        <Text
          variant="subHeading"
          color="foreground"
          as="span"
          className="block min-w-0 flex-1 truncate text-nowrap break-normal"
        >
          {item.name}
        </Text>
      </div>
      <div className="ml-4 flex justify-end gap-x-2">
        {showInlineEye && (
          <Eye className={cn(TREE_ICON_CLASS, "text-blue-500")} />
        )}
        {isCritical && (
          <TooltipWrapper message={getMatterImageTypeMessage(item)}>
            <div className="ml-auto flex items-center gap-2">
              <Text variant="subHeading" as="span" className="ml-auto">
                <CircleAlert className={TREE_ICON_CLASS} />
              </Text>
            </div>
          </TooltipWrapper>
        )}
      </div>
    </div>
  );

  return renderContextMenu ? renderContextMenu(item, trigger) : trigger;
}

type FolderComponentProps = {
  folder: RepoItem | undefined;
  isExpanded: boolean;
  isSelected: boolean;
  children: RepoItem[];
  level: number;
  onSelectItem: (item: RepoItem) => void;
  toggleFolder: (folderId: string, e: React.MouseEvent) => void;
  onMoveItem: (
    item: RepoItem,
    targetFolderId: string,
  ) => boolean | Promise<boolean>;
  onMoveItems?: (
    items: RepoItem[],
    targetFolderId: string,
  ) => void | Promise<void>;
  renderFolder: (folderId: string, level?: number) => React.JSX.Element | null;
  selectedItemId: string | null;
  selectedItemIds?: Set<string>;
  selectedDragItems?: RepoItem[];
  isSearching?: boolean;
  enabledFileTypes?: string[];
  sortKey?: FileSortKey;
  showCheckboxes?: boolean;
  checkboxState?: boolean | "indeterminate";
  previewLoadingItemId?: string | null;
  movingItemIds?: Set<string>;
  onToggleSelection?: (
    item: RepoItem,
    checked: boolean,
    shiftKey?: boolean,
  ) => void;
  getSelectionState?: (ids: Set<string>) => boolean | "indeterminate";
  enableDrag?: boolean;
  dragType?: string;
  renderContextMenu?: (item: RepoItem, trigger: ReactNode) => React.JSX.Element;
};

function FolderComponent({
  folder,
  isExpanded,
  isSelected,
  children,
  level,
  onSelectItem,
  toggleFolder,
  onMoveItem,
  onMoveItems,
  renderFolder,
  selectedItemId,
  selectedItemIds,
  selectedDragItems,
  isSearching = false,
  enabledFileTypes,
  sortKey,
  showCheckboxes,
  checkboxState,
  previewLoadingItemId = null,
  movingItemIds,
  onToggleSelection,
  getSelectionState,
  enableDrag = true,
  dragType = "FILE_SYSTEM_ITEM",
  renderContextMenu,
}: FolderComponentProps): React.JSX.Element | null {
  const [isOverFolderInternal, setIsOverFolderInternal] = useState(false);
  const [isOverFolder, setIsOverFolder] = useState(false);
  const [isCollectedOver, setIsCollectedOver] = useState(false);
  const lastShiftKeyRef = useRef(false);
  const isMoveLoading = movingItemIds?.has(folder?.id ?? "") ?? false;

  const dropSpec = {
    accept: dragType,
    drop: (payload: DragPayload, monitor: DropTargetMonitor) => {
      if (monitor.didDrop()) {
        return; // Don't handle if a nested target already handled it
      }

      if (onMoveItems && folder) {
        void onMoveItems(payload.items, folder.id);
        return;
      }

      if (folder) {
        payload.items.forEach((dragItem) => {
          void onMoveItem(dragItem, folder.id);
        });
      }
    },
    collect: (monitor: DropTargetMonitor) => ({
      isOver: monitor.isOver({ shallow: true }),
    }),
  };

  const [{ isOver: collectedIsOver }, drop] = useDrop(dropSpec);

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: dragType,
      item: folder ? ({ items: [folder] } satisfies DragPayload) : undefined,
      canDrag: enableDrag && !isMoveLoading,
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
    }),
    [dragType, enableDrag, folder, isMoveLoading],
  );

  const dragDropRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (enableDrag) {
        drag(node);
      }
      drop(node);
    },
    [drag, drop, enableDrag],
  );

  useEffect(() => {
    setIsCollectedOver(collectedIsOver);
  }, [collectedIsOver]);

  useEffect(() => {
    setIsOverFolderInternal(isCollectedOver);
  }, [isCollectedOver]);

  useEffect(() => {
    setIsOverFolder(isOverFolderInternal);
  }, [isOverFolderInternal]);

  if (!folder) return null;

  const trigger = (
    <div key={folder.id}>
      <div
        ref={dragDropRef}
        className={cn(
          TREE_ROW_CLASS,
          isSelected && "bg-primary/10 text-primary",
          isOverFolder && "bg-primary/5 border-primary/20 border",
          isDragging && "opacity-50",
          isMoveLoading && "cursor-wait",
        )}
        style={{ paddingLeft: `${getTreeIndent(level)}px` }}
        onClick={() => {
          onSelectItem(folder);
        }}
        title={folder.name}
      >
        {showCheckboxes && (
          <Checkbox
            checked={checkboxState ?? false}
            onClick={(event) => {
              event.stopPropagation();
              lastShiftKeyRef.current = event.shiftKey;
            }}
            onCheckedChange={(value) => {
              const nextChecked =
                value === "indeterminate" ? true : Boolean(value);
              const shiftKey = lastShiftKeyRef.current;
              lastShiftKeyRef.current = false;
              onToggleSelection?.(folder, nextChecked, shiftKey);
            }}
            className={getTreeCheckboxClassName(checkboxState)}
            aria-label={`Select ${folder.name}`}
          />
        )}
        <ChevronRight
          className={cn(
            "mr-1 transition-transform",
            TREE_ICON_CLASS,
            isExpanded && "rotate-90",
          )}
          onClick={(e) => {
            toggleFolder(folder.id, e);
          }}
        />
        <div className={TREE_CONTENT_CLASS}>
          {isMoveLoading ? (
            <LoadingSpinner className={cn(TREE_ICON_CLASS, "text-blue-500")} />
          ) : folder.kind === "share-folder" ? (
            <FolderInput className={cn(TREE_ICON_CLASS, "text-blue-400")} />
          ) : (
            <Folder className={cn(TREE_ICON_CLASS, "text-amber-500")} />
          )}
          <TooltipWrapper message={folder.name}>
            <Text
              variant="subHeading"
              color="foreground"
              as="span"
              className={TREE_TEXT_CLASS}
            >
              {folder.name}
            </Text>
          </TooltipWrapper>
        </div>
      </div>

      {isExpanded && (
        <div>
          {children.length === 0 ? (
            <div
              className="px-2 py-1 italic"
              style={{ paddingLeft: `${getTreeIndent(level + 1)}px` }}
            >
              <Text variant="subHeading" as="span">
                {isSearching ? "No results found" : "This folder is empty"}
              </Text>
            </div>
          ) : (
            <>
              {/* Render subfolders first */}
              {children
                .filter((item) => item.isFolder)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((item) => renderFolder(item.id, level + 1))}

              {/* Then render files */}
              {(sortKey
                ? sortFiles(
                    children.filter((item) => !item.isFolder),
                    sortKey,
                  )
                : children
                    .filter((item) => !item.isFolder)
                    .sort((a, b) => a.name.localeCompare(b.name))
              ).map((item) => (
                <FileTreeItem
                  key={item.id}
                  item={item}
                  level={level}
                  isSelected={
                    Boolean(showCheckboxes) &&
                    Boolean(selectedItemIds?.has(item.id))
                  }
                  dragItems={selectedDragItems}
                  isPreviewed={!item.isFolder && item.id === selectedItemId}
                  isPreviewLoading={item.id === previewLoadingItemId}
                  isMoveLoading={movingItemIds?.has(item.id)}
                  onSelect={onSelectItem}
                  disabled={
                    enabledFileTypes &&
                    !enabledFileTypes.includes(
                      getFileNameParts(item.name).fileExtension,
                    )
                  }
                  showCheckboxes={showCheckboxes}
                  checkboxState={
                    showCheckboxes
                      ? getSelectionState
                        ? getSelectionState(new Set([item.id]))
                        : false
                      : false
                  }
                  onToggleSelection={onToggleSelection}
                  enableDrag={enableDrag}
                  dragType={dragType}
                  renderContextMenu={renderContextMenu}
                />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );

  return renderContextMenu ? renderContextMenu(folder, trigger) : trigger;
}
